import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { RefreshCcw, Shield, ShieldX, Users, Calendar, Eye, Undo } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { formatDateToDDMMYYYY } from '@/lib/utils';

interface DojoCode {
  id: string;
  code: string;
  dojo_id: string;
  created_by: string;
  created_at: string;
  is_active: boolean;
  dojo_name: string;
  student_count: number;
}

interface StudentRegistration {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
}

const DojoCodesManagement: React.FC = () => {
  useUiLanguage();
  const [dojoCodes, setDojoCodes] = useState<DojoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [regenerateLoading, setRegenerateLoading] = useState<string | null>(null);
  const [revokeLoading, setRevokeLoading] = useState<string | null>(null);
  const [restoreLoading, setRestoreLoading] = useState<string | null>(null);
  const [selectedCodeStudents, setSelectedCodeStudents] = useState<StudentRegistration[]>([]);
  const [studentsDialogOpen, setStudentsDialogOpen] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);

  useEffect(() => {
    fetchDojoCodes();
  }, []);

  const fetchDojoCodes = async () => {
    try {
      setLoading(true);

      // Fetch dojo codes with related dojo information and student counts
      const { data, error } = await supabase
        .from('dojo_codes')
        .select(`
          id,
          code,
          dojo_id,
          created_by,
          created_at,
          is_active,
          dojos!inner(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get student counts for each dojo
      const dojoIds = data?.map(dc => dc.dojo_id) || [];
      const { data: studentCounts, error: countError } = await supabase
        .from('profiles')
        .select('dojo_id')
        .eq('role', 'student')
        .in('dojo_id', dojoIds);

      if (countError) throw countError;

      // Count students per dojo
      const studentCountMap = studentCounts?.reduce((acc, student) => {
        acc[student.dojo_id] = (acc[student.dojo_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // Map the data with student counts
      const mappedData: DojoCode[] = data?.map(dc => ({
        id: dc.id,
        code: dc.code,
        dojo_id: dc.dojo_id,
        created_by: dc.created_by,
        created_at: dc.created_at,
        is_active: dc.is_active,
        dojo_name: (dc.dojos as any).name,
        student_count: studentCountMap[dc.dojo_id] || 0
      })) || [];

      setDojoCodes(mappedData);
    } catch (error) {
      console.error('Error fetching dojo codes:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to fetch dojo codes"),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateCode = async (dojoId: string, currentCodeId: string) => {
    setRegenerateLoading(currentCodeId);
    try {
      // Use the RPC function to regenerate code
      const { data, error } = await supabase
        .rpc('regenerate_dojo_join_code', {
          target_dojo_id: dojoId
        });

      if (error) throw error;

      // Type cast the response
      const result = data as { success: boolean; dojo_name: string; old_code: string; new_code: string };

      toast({
        title: uiText("Code Regenerated"),
        description: uiText("New dojo code generated for {{value0}}: {{value1}}", { value0: result.dojo_name, value1: result.new_code }),
      });

      // Refresh the list
      await fetchDojoCodes();
    } catch (error: any) {
      console.error('Error regenerating code:', error);
      toast({
        title: uiText("Error"),
        description: error.message || 'Failed to regenerate dojo code',
        variant: 'destructive'
      });
    } finally {
      setRegenerateLoading(null);
    }
  };

  const handleRevokeCode = async (codeId: string) => {
    setRevokeLoading(codeId);
    try {
      // Use the RPC function to revoke code
      const { data, error } = await supabase
        .rpc('revoke_dojo_code', {
          code_id: codeId
        });

      if (error) throw error;

      // Type cast the response
      const result = data as { success: boolean; code: string; dojo_name: string };

      toast({
        title: uiText("Code Revoked"),
        description: uiText("Dojo code {{value0}} for {{value1}} has been deactivated", { value0: result.code, value1: result.dojo_name }),
      });

      // Refresh the list
      await fetchDojoCodes();
    } catch (error: any) {
      console.error('Error revoking code:', error);
      toast({
        title: uiText("Error"),
        description: error.message || 'Failed to revoke dojo code',
        variant: 'destructive'
      });
    } finally {
      setRevokeLoading(null);
    }
  };

  const handleRestoreCode = async (codeId: string) => {
    setRestoreLoading(codeId);
    try {
      // Use the RPC function to restore code
      const { data, error } = await supabase
        .rpc('restore_dojo_code', {
          code_id: codeId
        });

      if (error) throw error;

      // Type cast the response
      const result = data as { success: boolean; code: string; dojo_name: string };

      toast({
        title: uiText("Code Restored"),
        description: uiText("Dojo code {{value0}} for {{value1}} has been reactivated", { value0: result.code, value1: result.dojo_name }),
      });

      // Refresh the list
      await fetchDojoCodes();
    } catch (error: any) {
      console.error('Error restoring code:', error);
      toast({
        title: uiText("Error"),
        description: error.message || 'Failed to restore dojo code',
        variant: 'destructive'
      });
    } finally {
      setRestoreLoading(null);
    }
  };

  const handleViewStudents = async (dojoId: string) => {
    setStudentsLoading(true);
    setStudentsDialogOpen(true);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, created_at')
        .eq('dojo_id', dojoId)
        .eq('role', 'student')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setSelectedCodeStudents(data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to fetch student registrations"),
        variant: 'destructive'
      });
      setSelectedCodeStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge className="bg-green-100 text-green-800 border border-green-300">
        <Shield className="h-3 w-3 mr-1" />
        {uiText("Active")}</Badge>
    ) : (
      <Badge className="bg-red-100 text-red-800 border border-red-300">
        <ShieldX className="h-3 w-3 mr-1" />
        {uiText("Revoked")}</Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2 text-muted-foreground">{uiText("Loading dojo codes...")}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-200">{uiText("Dojo Codes Management")}</h3>
          <p className="text-sm text-slate-400">{uiText("Manage and monitor all dojo join codes")}</p>
        </div>
        <Button onClick={fetchDojoCodes} variant="outline" size="sm">
          <RefreshCcw className="h-4 w-4 mr-2" />
          {uiText("Refresh")}</Button>
      </div>

      <Card className="bg-slate-800/30 border-slate-700">
        <CardHeader>
          <CardTitle className="text-slate-200">{uiText("All Dojo Codes")}</CardTitle>
          <CardDescription className="text-slate-400">
            {uiText("Total codes:")}{' '}{dojoCodes.length} {' '}{uiText("• Active:")}{' '}{dojoCodes.filter(dc => dc.is_active).length} {' '}{uiText("• Revoked:")}{' '}{dojoCodes.filter(dc => !dc.is_active).length}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-6">
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300 text-xs sm:text-sm px-2 sm:px-4">{uiText("Code")}</TableHead>
                  <TableHead className="text-slate-300 text-xs sm:text-sm px-2 sm:px-4 hidden sm:table-cell">{uiText("Dojo")}</TableHead>
                  <TableHead className="text-slate-300 text-xs sm:text-sm px-2 sm:px-4">{uiText("Status")}</TableHead>
                  <TableHead className="text-slate-300 text-xs sm:text-sm px-2 sm:px-4 hidden md:table-cell">{uiText("Students")}</TableHead>
                  <TableHead className="text-slate-300 text-xs sm:text-sm px-2 sm:px-4 hidden lg:table-cell">{uiText("Created By")}</TableHead>
                  <TableHead className="text-slate-300 text-xs sm:text-sm px-2 sm:px-4 hidden lg:table-cell">{uiText("Created")}</TableHead>
                  <TableHead className="text-slate-300 text-xs sm:text-sm px-2 sm:px-4">{uiText("Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dojoCodes.map((dojoCode) => (
                  <TableRow key={dojoCode.id} className="border-slate-700">
                    <TableCell className="font-mono text-slate-200 font-semibold text-xs sm:text-sm px-2 sm:px-4">
                      <div className="flex flex-col">
                        <span>{dojoCode.code}</span>
                        <span className="sm:hidden text-xs text-slate-400 mt-1">{dojoCode.dojo_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300 text-xs sm:text-sm px-2 sm:px-4 hidden sm:table-cell">
                      {dojoCode.dojo_name}
                    </TableCell>
                    <TableCell className="px-2 sm:px-4">
                      <div className="flex flex-col gap-1">
                        {getStatusBadge(dojoCode.is_active)}
                        <div className="md:hidden flex items-center gap-1 text-xs text-slate-400">
                          <Users className="h-3 w-3" />
                          <span>{dojoCode.student_count}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell px-2 sm:px-4">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-slate-400" />
                        <span className="text-slate-300">{dojoCode.student_count}</span>
                        {dojoCode.student_count > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewStudents(dojoCode.dojo_id)}
                            className="h-6 px-2 text-xs text-slate-400 hover:text-slate-200"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            {uiText("View")}</Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300 text-xs sm:text-sm px-2 sm:px-4 hidden lg:table-cell">
                      {dojoCode.created_by}
                    </TableCell>
                    <TableCell className="text-slate-300 text-xs sm:text-sm px-2 sm:px-4 hidden lg:table-cell">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-slate-400" />
                        {formatDateToDDMMYYYY(dojoCode.created_at)}
                      </div>
                    </TableCell>
                    <TableCell className="px-2 sm:px-4">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2">
                        {dojoCode.is_active && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={regenerateLoading === dojoCode.id}
                                className="text-slate-300 border-slate-600 hover:bg-slate-700 w-full sm:w-auto text-xs sm:text-sm h-7 sm:h-8"
                              >
                                {regenerateLoading === dojoCode.id ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b border-current" />
                                ) : (
                                  <RefreshCcw className="h-3 w-3" />
                                )}
                                <span className="sm:hidden ml-2">{uiText("Regenerate")}</span>
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-slate-800 border-slate-700">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-slate-200">
                                  {uiText("Regenerate Dojo Code")}</AlertDialogTitle>
                                <AlertDialogDescription className="text-slate-400">
                                  {uiText("This will deactivate the current code \"")}{dojoCode.code}{uiText("\" and generate a new one for")}{' '}{dojoCode.dojo_name}{uiText(". Students using the old code will no longer be able to register.")}</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-slate-700 text-slate-200 border-slate-600">
                                  {uiText("Cancel")}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleRegenerateCode(dojoCode.dojo_id, dojoCode.id)}
                                  className="bg-indigo-600 hover:bg-indigo-700"
                                >
                                  {uiText("Regenerate Code")}</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}

                        {dojoCode.is_active && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="destructive"
                                size="sm"
                                disabled={revokeLoading === dojoCode.id}
                                className="bg-red-600/20 text-red-400 border-red-600 hover:bg-red-600/30 w-full sm:w-auto text-xs sm:text-sm h-7 sm:h-8"
                              >
                                {revokeLoading === dojoCode.id ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b border-current" />
                                ) : (
                                  <ShieldX className="h-3 w-3" />
                                )}
                                <span className="sm:hidden ml-2">{uiText("Revoke")}</span>
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-slate-800 border-slate-700">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-slate-200">
                                  {uiText("Revoke Dojo Code")}</AlertDialogTitle>
                                <AlertDialogDescription className="text-slate-400">
                                  {uiText("This will permanently deactivate the code \"")}{dojoCode.code}{uiText("\" for")}{' '}{dojoCode.dojo_name}{uiText(". New students will no longer be able to register using this code.")}</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-slate-700 text-slate-200 border-slate-600">
                                  {uiText("Cancel")}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleRevokeCode(dojoCode.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  {uiText("Revoke Code")}</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                         )}

                         {!dojoCode.is_active && (
                           <AlertDialog>
                             <AlertDialogTrigger asChild>
                               <Button
                                 variant="outline"
                                 size="sm"
                                 disabled={restoreLoading === dojoCode.id}
                                 className="bg-green-600/20 text-green-400 border-green-600 hover:bg-green-600/30 w-full sm:w-auto text-xs sm:text-sm h-7 sm:h-8"
                               >
                                 {restoreLoading === dojoCode.id ? (
                                   <div className="animate-spin rounded-full h-3 w-3 border-b border-current" />
                                 ) : (
                                   <Undo className="h-3 w-3" />
                                 )}
                                 <span className="sm:hidden ml-2">{uiText("Restore")}</span>
                               </Button>
                             </AlertDialogTrigger>
                             <AlertDialogContent className="bg-slate-800 border-slate-700">
                               <AlertDialogHeader>
                                 <AlertDialogTitle className="text-slate-200">
                                   {uiText("Restore Dojo Code")}</AlertDialogTitle>
                                 <AlertDialogDescription className="text-slate-400">
                                   {uiText("This will reactivate the code \"")}{dojoCode.code}{uiText("\" for")}{' '}{dojoCode.dojo_name}{uiText(". Students will be able to register using this code again.")}</AlertDialogDescription>
                               </AlertDialogHeader>
                               <AlertDialogFooter>
                                 <AlertDialogCancel className="bg-slate-700 text-slate-200 border-slate-600">
                                   {uiText("Cancel")}</AlertDialogCancel>
                                 <AlertDialogAction
                                   onClick={() => handleRestoreCode(dojoCode.id)}
                                   className="bg-green-600 hover:bg-green-700"
                                 >
                                   {uiText("Restore Code")}</AlertDialogAction>
                               </AlertDialogFooter>
                             </AlertDialogContent>
                           </AlertDialog>
                         )}
                       </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Students Dialog */}
      <Dialog open={studentsDialogOpen} onOpenChange={setStudentsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-slate-200">{uiText("Student Registrations")}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {uiText("Students who registered using this dojo code")}</DialogDescription>
          </DialogHeader>

          {studentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2 text-slate-400">{uiText("Loading students...")}</span>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-300">{uiText("Name")}</TableHead>
                    <TableHead className="text-slate-300">{uiText("Email")}</TableHead>
                    <TableHead className="text-slate-300">{uiText("Registered")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedCodeStudents.map((student) => (
                    <TableRow key={student.id} className="border-slate-700">
                      <TableCell className="text-slate-200">
                        {student.first_name} {student.last_name}
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {student.email}
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {formatDateToDDMMYYYY(student.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {selectedCodeStudents.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  {uiText("No students have registered with this dojo code yet.")}</div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setStudentsDialogOpen(false)}
              className="bg-slate-700 text-slate-200 hover:bg-slate-600"
            >
              {uiText("Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DojoCodesManagement;